import { useEffect, useState } from "react";
import { RegisterEditVerificationMethod } from "react-common-ui-library/FPACT/Business";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router";
import { useNavigate } from "react-router-dom";
import { AccountAlreadyExistsModal } from "react-common-ui-library";
import {
  CONSUMER_VIEW,
  CONTACT_NUMBER,
  EMAIL,
  EMAIL_LANGUAGE,
  EMAIL_OR_PHONE_ALREADY_EXISTS,
  LOGIN_ROUTE,
  LOGIN_VERIFICATION_METHOD_ROUTE,
  MY_PROFILE_ACCOUNT_SETTINGS_ROUTE,
  REGISTER_ACTION,
  SECURITY_PREFERENCES_ROUTE,
  VERIFY_ACCOUNT_ROUTE,
} from "../../Constants/CommonConstants";
import { PHONE_NUMBER } from "../../Constants/NameAndContactConstants";
import {
  SLICE_LOGIN,
  THUNK_POST_REGISTER_EDIT_VERIFICATION_METHOD,
} from "../../Constants/SliceAndThunkConstants";
import {
  ADD_OR_EDIT_VERIFICATION,
  OTP_SENT_SUCCESSFULLY,
  RESEND_OTP_LIMIT_EXHAUSTED,
  SEND_OTP_DESCRIPTION,
} from "../../Constants/VerifyAccountConstants";
import {
  clearLoginApiResponse,
  postRegisterEditVerificationMethod,
  resetLoginSendOTPDescription,
  resetToastFlow,
  updateAddEditVerificationValues,
} from "../../Features/LoginSlice";
import { manageApiResponseSelector } from "../../Selectors/ApiResponseSelector";
import { manageLoginSelector } from "../../Selectors/LoginSelector";
import ConfigService from "../../Services/ConfigService";
import { PHONE_HELPER_TEXT } from "../../Constants/LoginVerificationMethodsConstants";
import { manageReferenceDataSelector } from "../../Selectors/ReferenceDataSelector";
import {
  fetchEmailLanguageOptions,
  handlePayloadForEditOrRegisterVerification,
} from "../../Services/CommonServices";
import { saveAddEditEmailLanguage } from "../../Features/MyProfileSlice";
import { manageMyProfileSelector } from "../../Selectors/MyProfileSelector";

/**
 * @description The screen that is displayed when user choose to add or edit verification method
 * @returns {ReactNode} Renders the register or edit verification method screen from common ui library
 */
const AddEditVerificationMethod = () => {
  const navigate = useNavigate();

  const dispatch = useDispatch();

  const location = useLocation();

  const [isAccountAlreadyExists, setIsAccountAlreadyExists] = useState(false);

  const { flow, previousScreen, method, emailLanguage } = location.state;

  const { addEditEmail, addEditPhone } = useSelector((state) =>
    manageLoginSelector(state)
  );

  const { addEditEmailLanguage } = useSelector((state) =>
    manageMyProfileSelector(state)
  );

  const { preferredLanguage } = useSelector((state) =>
    manageReferenceDataSelector(state)
  );

  const contactNumber = ConfigService(CONTACT_NUMBER);

  const { apiResponses } = useSelector((state) =>
    manageApiResponseSelector(
      state,
      SLICE_LOGIN,
      THUNK_POST_REGISTER_EDIT_VERIFICATION_METHOD
    )
  );

  const { isSuccess, description, isFailure } = apiResponses;

  const backButtonOnClickHandler = () => {
    previousScreen === LOGIN_VERIFICATION_METHOD_ROUTE
      ? navigate(`/${LOGIN_ROUTE}/${LOGIN_VERIFICATION_METHOD_ROUTE}`)
      : navigate(
          `/${MY_PROFILE_ACCOUNT_SETTINGS_ROUTE}/${SECURITY_PREFERENCES_ROUTE}`
        );
  };

  const onCancelButtonClickHandler = () => {
    setIsAccountAlreadyExists(false);
    dispatch(
      clearLoginApiResponse(THUNK_POST_REGISTER_EDIT_VERIFICATION_METHOD)
    );
  };

  const navigateToLogIn = () => {
    onCancelButtonClickHandler();
    navigate(`/${LOGIN_ROUTE}`);
  };

  const checkIfEmailLanguageChanged = (data) => {
    if (addEditEmailLanguage) {
      return (
        addEditEmailLanguage.description !== data[EMAIL_LANGUAGE].description
      );
    }

    return emailLanguage.description !== data[EMAIL_LANGUAGE].description;
  };

  const sendCodeButtonOnClickHandler = (data) => {
    if (flow) {
      const verificationMethod = flow === EMAIL ? EMAIL : PHONE_NUMBER;
      const isEmailLanguageChanged = checkIfEmailLanguageChanged(data);

      const payload = handlePayloadForEditOrRegisterVerification(
        data,
        verificationMethod,
        flow,
        method,
        addEditEmail,
        isEmailLanguageChanged
      );

      dispatch(
        updateAddEditVerificationValues({ [verificationMethod]: data[flow] })
      );
      dispatch(saveAddEditEmailLanguage(data[EMAIL_LANGUAGE]));
      dispatch(
        postRegisterEditVerificationMethod({
          data: payload,
          apiAction: REGISTER_ACTION,
        })
      );
      dispatch(resetLoginSendOTPDescription());
    }
  };

  useEffect(() => {
    if (
      isSuccess &&
      (description === OTP_SENT_SUCCESSFULLY ||
        description === RESEND_OTP_LIMIT_EXHAUSTED)
    ) {
      dispatch(
        clearLoginApiResponse(THUNK_POST_REGISTER_EDIT_VERIFICATION_METHOD)
      );
      previousScreen === LOGIN_VERIFICATION_METHOD_ROUTE
        ? navigate(`/${LOGIN_ROUTE}/${VERIFY_ACCOUNT_ROUTE}`, {
            state: {
              previousScreen: ADD_OR_EDIT_VERIFICATION,
              method: method,
            },
          })
        : navigate(
            `/${MY_PROFILE_ACCOUNT_SETTINGS_ROUTE}/${VERIFY_ACCOUNT_ROUTE}`,
            {
              state: {
                previousScreen: SECURITY_PREFERENCES_ROUTE,
                method: method,
              },
            }
          );
    }
    if (isSuccess && description === "EMAIL_LANGUAGE_UPDATED_SUCCESSFULLY") {
      dispatch(
        clearLoginApiResponse(THUNK_POST_REGISTER_EDIT_VERIFICATION_METHOD)
      );
      previousScreen === LOGIN_VERIFICATION_METHOD_ROUTE
        ? navigate(`/${LOGIN_ROUTE}/${LOGIN_VERIFICATION_METHOD_ROUTE}  `)
        : navigate(
            `/${MY_PROFILE_ACCOUNT_SETTINGS_ROUTE}/${SECURITY_PREFERENCES_ROUTE}`
          );
    }
    if (isFailure && description === EMAIL_OR_PHONE_ALREADY_EXISTS) {
      setIsAccountAlreadyExists(true);
    }

    dispatch(resetToastFlow());
  }, [description, dispatch, isSuccess, navigate, previousScreen, isFailure]);

  return (
    <>
      <RegisterEditVerificationMethod
        verificationMethod={flow}
        verificationMethodValue={flow === EMAIL ? addEditEmail : addEditPhone}
        backButtonOnClickHandler={backButtonOnClickHandler}
        sendCodeButtonOnClickHandler={sendCodeButtonOnClickHandler}
        isDisabled={
          sessionStorage.getItem(SEND_OTP_DESCRIPTION) ===
          RESEND_OTP_LIMIT_EXHAUSTED
        }
        helperText={PHONE_HELPER_TEXT}
        method={method}
        emailLanguageOptions={fetchEmailLanguageOptions(preferredLanguage)}
        defaultEmailLanguage={addEditEmailLanguage || emailLanguage}
      />
      <AccountAlreadyExistsModal
        userView={CONSUMER_VIEW}
        open={isAccountAlreadyExists}
        contactNumber={contactNumber}
        navigateToLogIn={navigateToLogIn}
        onCancelButtonClickHandler={onCancelButtonClickHandler}
      />
    </>
  );
};

export default AddEditVerificationMethod;
